import React, { useEffect, useRef, useState } from "react";

/**
 * Lazy-loads an image when it intersects a scroll root (IntersectionObserver).
 * Use inside a scrollable container; pass the same ref as `scrollerRef`.
 */
export default function LazyScrollImage({
  scrollerRef,
  id,
  src,
  alt,
  className,
  onClick,
  eager = false,
  frameClass,
  pendingClass,
  fallbackClass,
  onEagerDone,
  countEagerErrorTowardReady = false,
}) {
  const imgRef = useRef(null);
  const [inView, setInView] = useState(eager);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (eager) return;
    if (inView) return;
    const root = scrollerRef?.current;
    const node = imgRef.current;
    if (!node) return;

    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { root, rootMargin: "300px 0px", threshold: 0.01 }
    );

    obs.observe(node);
    return () => obs.disconnect();
  }, [eager, inView, scrollerRef]);

  const wrapperClass = `${frameClass}${!inView ? ` ${pendingClass}` : ""}`;

  return (
    <div className={wrapperClass}>
      <img
        ref={imgRef}
        src={inView ? src : undefined}
        data-src={src}
        alt={alt}
        className={className}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchpriority={eager ? "high" : "low"}
        onClick={onClick}
        onLoad={() => {
          if (eager) onEagerDone?.(id);
        }}
        onError={() => {
          setFailed(true);
          if (eager && countEagerErrorTowardReady) onEagerDone?.(id);
        }}
      />
      {failed && (
        <div className={fallbackClass}>Image failed to load</div>
      )}
    </div>
  );
}
